import { DateCalendar as MuiDateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// The first MUI X component behind the facade, and the only place a date library lives: the
// wrapper owns the LocalizationProvider, the dayjs adapter and the locale, so a slice imports a
// component and wires nothing. Its API speaks plain `YYYY-MM-DD` strings — callers never touch a
// Dayjs object, the same reason TextField emits a string instead of MUI's synthetic event. That
// keeps dayjs out of every app's source: the day the calendar hands back IS the day the write
// sends. dayjs parses a bare YYYY-MM-DD as LOCAL midnight (unlike `new Date()`, which reads it as
// UTC), so the value needs no parse plugin and never shifts a day.
// Semantic component → no `sx`. MUI's DateCalendar is a fixed 320px wide, which overflows the
// «Acciones» card (a third of the row) below ~1200px viewport, so the root is allowed to shrink to
// the day grid's own floor (~276px) and scrolls inside itself below that rather than bleeding out.
// TODO: i18n — the locale is pinned to `es` until Phase 1.
const WIRE_FORMAT = 'YYYY-MM-DD';

export interface DateCalendarProps {
  // A floating calendar day, `YYYY-MM-DD` — no instant, no timezone.
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  disabled?: boolean;
  // Takes focus on mount. For a calendar REVEALED by a disclosure (the «Acciones» inline confirm
  // step), the control that opened it has just unmounted, so without this focus falls to <body> and
  // a keyboard agent has to re-traverse the card to reach the grid.
  autoFocus?: boolean;
  'aria-label'?: string;
}

export function DateCalendar({
  value,
  onChange,
  minDate,
  disabled = false,
  autoFocus = false,
  ...rest
}: DateCalendarProps) {
  const ariaLabel = rest['aria-label'];
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box
        role="group"
        // Forward optionals only when set: the project runs with exactOptionalPropertyTypes.
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        sx={{ minWidth: 0, overflowX: 'auto' }}
      >
        <MuiDateCalendar
          value={dayjs(value)}
          disabled={disabled}
          autoFocus={autoFocus}
          {...(minDate !== undefined ? { minDate: dayjs(minDate) } : {})}
          onChange={(next) => {
            // The grid can only hand back a day inside the allowed range, but a null/invalid value
            // is representable in MUI's type — swallow it rather than emitting a broken string.
            if (next?.isValid() === true) onChange(next.format(WIRE_FORMAT));
          }}
          sx={{ width: '100%', maxWidth: 320 }}
        />
      </Box>
    </LocalizationProvider>
  );
}
